import type { TFunction } from "i18next";
import { Funnel } from "lucide-react";

import { Button } from "@/components/ui/v2/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/v2/dropdown-menu";

interface SegmentOption {
  id: string;
  name: string;
  count: number;
}

interface Props {
  segments: SegmentOption[];
  filterActive: boolean;
  defaultTopN: number;
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  reset: () => void;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  isFetchingNextPage: boolean;
  t: TFunction;
}

export function SegmentCountFilterMenu({
  segments,
  filterActive,
  defaultTopN,
  isSelected,
  toggle,
  reset,
  onScroll,
  isFetchingNextPage,
  t,
}: Props) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={t("home.segmentCount.filter.aria")}
          className="relative"
        >
          <Funnel className="size-4" />
          {t("common.filterSelection")}
          {filterActive && (
            <span
              aria-hidden
              className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-primary"
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" sideOffset={6}>
        <div className="flex items-center justify-between gap-2 px-2 pb-1">
          <span className="text-sm font-semibold">
            {t("home.segmentCount.filter.title")}
          </span>
          {filterActive && (
            <Button type="button" onClick={reset} variant="link" size="sm">
              {t("home.segmentCount.filter.reset", { max: defaultTopN })}
            </Button>
          )}
        </div>
        <div onScroll={onScroll} className="max-h-72 overflow-y-auto">
          {segments.map((item) => (
            <DropdownMenuCheckboxItem
              key={item.id}
              checked={isSelected(item.id)}
              onCheckedChange={() => toggle(item.id)}
              onSelect={(e: Event) => e.preventDefault()}
              count={item.count}
            >
              {item.name}
            </DropdownMenuCheckboxItem>
          ))}
          {isFetchingNextPage && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              {t("common.loading")}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
